import { Component } from "@theme/component";

const PREFILL_KEY = "newsletter-signup:email";

/**
 * @typedef {object} NewsletterSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLElement} error
 * @property {HTMLElement} [success]
 * @property {HTMLInputElement[]} [newsletters]
 */

/**
 * Validates the signup before Shopify's customer form posts it, with every ticked newsletter as a customer tag.
 * Without newsletter entries the page is a plain signup. After a successful post only the form and success refs
 * render, so the other refs exist whenever their events fire.
 *
 * @extends {Component<NewsletterSignupRefs>}
 */
class NewsletterSignupComponent extends Component {
  requiredRefs = ["form"];

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener("formdata", this.#joinTags);
    this.#prefillEmail();
    this.refs.success?.focus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener("formdata", this.#joinTags);
  }

  /** The footer signup (blocks/email-signup.liquid) hands over the visitor's address once, through sessionStorage. */
  #prefillEmail() {
    let email = null;

    try {
      email = sessionStorage.getItem(PREFILL_KEY);
      sessionStorage.removeItem(PREFILL_KEY);
    } catch {}

    if (email && this.refs.email && !this.refs.email.value) this.refs.email.value = email;
  }

  /** @param {SubmitEvent} event */
  submit(event) {
    const problem = this.#validate();

    this.#clearError();

    if (!problem) return;

    event.preventDefault();
    this.#showError(...problem);
  }

  /** @returns {[string | undefined, HTMLInputElement?] | undefined} */
  #validate() {
    const { email, newsletters = [] } = this.refs;

    if (!email.checkValidity()) return [this.dataset.errorEmail, email];
    if (newsletters.length > 0 && !newsletters.some((input) => input.checked)) return [this.dataset.errorNewsletter];
  }

  /**
   * Shopify keeps only the last `contact[tags]` field, so the ticked tags go out as one comma-separated value.
   *
   * @param {FormDataEvent} event
   */
  #joinTags = ({ formData }) => {
    const tags = formData.getAll("contact[tags]");

    if (tags.length > 1) formData.set("contact[tags]", tags.join(","));
  };

  /**
   * @param {string | undefined} message
   * @param {HTMLInputElement} [field]
   */
  #showError(message, field) {
    const { error } = this.refs;

    error.textContent = message ?? "";
    error.hidden = false;

    if (field) {
      field.setAttribute("aria-invalid", "true");
      field.setAttribute("aria-describedby", error.id);
      field.focus();
    }
  }

  #clearError() {
    this.refs.error.hidden = true;

    for (const field of this.querySelectorAll("[aria-invalid]")) {
      field.removeAttribute("aria-invalid");
      field.removeAttribute("aria-describedby");
    }
  }
}

if (!customElements.get("newsletter-signup-component")) {
  customElements.define("newsletter-signup-component", NewsletterSignupComponent);
}
